// @ts-ignore
import StackdriverErrorReporter from "stackdriver-errors-js";
import { ErrorReporter } from "./ErrorReporter";

/** Any regex here will squelch matching Stackdriver error messages. */
const SUPPRESSED_STACKDRIVER_ERROR_REGEXES = [
  // A user trying to edit a notebook without access is not noteworthy to log.
  /Error: User doesn't have Write access to the kernel\./,
  /Error: Permission denied/,
  // Competition acceptance error is not exceptional.
  /To see the data you need to agree to the competition rules/,
  // Do not report 429 / 404 errors.
  /\[ResponseMiddlewares.enforceOk\] response was not ok \| (429|404)/,
  // Any useful data for this error is already lost at this point.
  // We should work on fixing this issue in Roslyn or at the middle tier.
  /Unexpected token e in JSON at position [0-9]+/,
  // stackdriver-errors-js throws some internal errors that we should not log.
  // 4xxs have a chance to loop (429 -> error -> report -> 429 -> ...)
  /Error: 4[0-9]+ http response on stackdriver report.*/,
  // Poor network conditions will not be resolved immediately.
  /Error: network error on stackdriver report.*/,
];

/**
 * A function wrapper for StackdriverErrorReporter.prototype.report, which
 * filters out some noisy errors before logging to go/kaggle-fe-errors.
 *
 * @param this Special TS syntax required avoid an implicit 'any' shadow for
 *     'this'. See http://shortn/_3mjEv56mPc. It's not a param, and the first
 *     argument passed to the function will not be assigned to 'this'.
 * @param args An array of all arguments passed to the function.
 */
function filteredReport(this: ErrorReporter, ...args: any[]): Promise<any> {
  // Sample reported errors at 10% volume to avoid exceeding Stackdriver
  // request quota.
  if (Math.random() < 0.9) {
    return Promise.resolve();
  }
  const error: Error = args[0];
  const message = error.message || ((error as any) as string);
  if (typeof message === "string") {
    // Ignore all matching squelched messages.
    if (
      SUPPRESSED_STACKDRIVER_ERROR_REGEXES.find(regex => regex.test(message))
    ) {
      return Promise.resolve();
    }
    // Ignore bots.
    if (
      !window.navigator ||
      !window.navigator.userAgent ||
      window.navigator.userAgent.toLowerCase().includes("bot")
    ) {
      return Promise.resolve();
    }
  }
  // Not ignored. Pass off to the error reporter.
  return StackdriverErrorReporter.prototype.report.apply(this, args);
}

/** Patches Stackdriver error reporter to allow filtering of non-exceptional errors. */
export const patchStackdriverErrorReporter = (reporter: ErrorReporter) => {
  reporter.report = filteredReport.bind(reporter);
};

/** Patches console.error to report error messages to Stackdriver. */
export const patchConsoleError = (reporter: ErrorReporter) => {
  const modifiedConsole = (baseConsole => {
    return {
      ...baseConsole,
      error: (...args: any[]) => {
        reporter.report(new Error(args.join(" ")));
        baseConsole.error(...args);
      },
    };
  })(window.console);
  window.console = modifiedConsole;
};
